import { UI, Utils } from 'common';
import { isArray } from 'lodash';
import React, { Component } from 'react';
import AccountCommunication from '../utils/AccountCommunication';

const { Session, UserCaps, UserRole, Util, withLayoutAndModalContexts } = Utils;
const {
	WrappedInputText,
	WrappedInputTextWithLabel,
	WrappedDropdownWithLabel,
	WrappedButton,
	WrappedCheckbox,
	WrappedInputNumberWithLabel,
} = UI;

class LocationGeneralSettings extends Component {
	constructor(props) {
		super(props);

		const {
			enterpriseLocation,
			regions = [],
			userCorpInfo = {},
			MAXIMUM_LIMIT_AMOUNT,
		} = props;
		const { AutoPurchase, MeterAccount } = enterpriseLocation;
		const { Enabled, PurchaseAmount, ThresholdAmount } = AutoPurchase;
		const { Capabilities, CorpId, Name, PerPrintLimit } = MeterAccount;

		/**
		 * Dev note:
		 * A corporation has 1 region called 'West' which has 'LA' location. Corporation's id is 1111, and
		 * the region's id is 2222. To unassign 'West' from 'LA' location, or set 'None' to 'LA' location,
		 * system sets location's id (refer to MeterAccount.CorpId) back to 1111 from 2222.
		 *
		 * In above example, it's important to determine the id for 'None' option as it's to associate the
		 * location with the corporation and deassociate the location from the region.
		 *
		 * The only way to get the corporation's id is to make a call getUserCorpInfo endpoint per the admin.
		 * In the response, if the admin is assigned to a region, then
		 * 		CorpId: < This is the region id >
		 * 		ParentCorpId: < This is the corporation id >
		 * And, if the admin is NOT assign to any region, then in the response,
		 * 		CorpId: < This is the corporation id >
		 * 		ParentCorpId: 0
		 */
		const rootCorporationId =
			userCorpInfo.ParentCorpId === 0
				? userCorpInfo.CorpId
				: userCorpInfo.ParentCorpId;

		// per WEBAPPS-25969, uncheck "Restrict To" checkbox should set per-print value to max
		const perPrintLimit =
			PerPrintLimit === MAXIMUM_LIMIT_AMOUNT ? null : PerPrintLimit;

		this.state = {
			// AutoPurchase properties
			Enabled,
			PurchaseAmount,
			ThresholdAmount,

			// MeterAccount properties
			Capabilities,
			CorpId,
			Name,
			PerPrintLimit: perPrintLimit === null ? 0 : perPrintLimit,

			// for UserCaps
			ucPurchasePostage: Util.hasBitSet(
				Capabilities,
				UserCaps.ucPurchasePostage
			),
			ucPurchaseFromStore: Util.hasBitSet(
				Capabilities,
				UserCaps.ucPurchaseFromStore
			),

			// reference code user cap renamed locally to avoid confusion, since boolean is reversed
			referenceCodeRequired: !Util.hasBitSet(
				Capabilities,
				UserCaps.ucReferenceCodeOptional
			),
			restrictToCheckbox: perPrintLimit !== null,
			ucWebBasedReports: Util.hasBitSet(
				Capabilities,
				UserCaps.ucWebBasedReports
			),

			// root corporation id
			rootCorporationId,

			// add 'None' option to Region dropdown
			regions: isArray(regions)
				? [
						{
							Id: rootCorporationId,
							Name: 'None',
						},
						...regions,
					]
				: [],

			// Blocks Save button while waiting for response
			isLoading: false,
		};
	}

	updateLocations = async updatedEnterpriseLocation => {
		const {
			callback,
			modalContext: { showError },
		} = this.props;
		const updatedLocations = [updatedEnterpriseLocation];

		this.setState({
			isLoading: true,
		});

		const response =
			await AccountCommunication.updateLocations(updatedLocations);

		this.setState({
			isLoading: false,
		});

		if (!AccountCommunication.isSuccessful(response)) {
			showError({
				response,
			});
		}

		callback(response);
	};

	handleInputChange = e => {
		const { name, value } = e.target;

		this.setState({
			[name]: value,
		});
	};

	onClickCheckbox = e => {
		let { Capabilities } = this.state;
		const { name, checked } = e.target;
		const capNames = [
			'ucPurchasePostage',
			'ucPurchaseFromStore',
			'referenceCodeRequired',
			'ucWebBasedReports',
		];

		// if checkbox name is a user cap
		if (capNames.indexOf(name) !== -1) {
			// special handling for reference code user cap because boolean value is reversed
			if (name === 'referenceCodeRequired') {
				Capabilities = checked
					? Util.removeBit(Capabilities, UserCaps.ucReferenceCodeOptional)
					: Util.setBit(Capabilities, UserCaps.ucReferenceCodeOptional);
			}

			// update the other capabilities
			Capabilities = checked
				? Util.setBit(Capabilities, UserCaps[name])
				: Util.removeBit(Capabilities, UserCaps[name]);
		}

		this.setState({
			[name]: checked,
			Capabilities,
		});
	};

	resetAutoPurchaseAndMeterAccountProperties = () => {
		// reset form and checkbox
		const { enterpriseLocation } = this.props;
		const { AutoPurchase, MeterAccount } = enterpriseLocation;
		const { Enabled, PurchaseAmount, ThresholdAmount } = AutoPurchase;
		const { Capabilities, CorpId, Name, PerPrintLimit } = MeterAccount;

		this.setState({
			// AutoPurchase properties
			Enabled,
			PurchaseAmount,
			ThresholdAmount,
			// MeterAccount properties
			Capabilities,
			CorpId,
			Name,
			PerPrintLimit,
			// for UserCaps
			ucPurchasePostage: Util.hasBitSet(
				Capabilities,
				UserCaps.ucPurchasePostage
			),
			ucPurchaseFromStore: Util.hasBitSet(
				Capabilities,
				UserCaps.ucPurchaseFromStore
			),
			// reference code user cap renamed locally to avoid confusion, since boolean is reversed
			referenceCodeRequired: !Util.hasBitSet(
				Capabilities,
				UserCaps.ucReferenceCodeOptional
			),
			restrictToCheckbox: !!PerPrintLimit,
			ucWebBasedReports: Util.hasBitSet(
				Capabilities,
				UserCaps.ucWebBasedReports
			),
		});
	};

	onSaveButtonClick = async () => {
		const {
			enterpriseLocation,
			parentPerPrintLimit,
			modalContext: { showError },
			MAXIMUM_LIMIT_AMOUNT,
		} = this.props;
		const {
			MeterAccount: { MeterNo },
			ParentCorpId,
			CustId,
		} = enterpriseLocation;
		const {
			// AutoPurchase properties
			Enabled,
			PurchaseAmount,
			ThresholdAmount,
			// MeterAccount properties
			Capabilities,
			CorpId,
			Name,
			PerPrintLimit,
			restrictToCheckbox,
			rootCorporationId,
		} = this.state;

		const updatedEnterpriseLocation = {
			...enterpriseLocation,
			AutoPurchase: {
				Enabled,
				PurchaseAmount,
				ThresholdAmount,
			},
			MeterAccount: {
				Capabilities,
				CorpId,
				MeterNo,
				Name,
				PerPrintLimit: restrictToCheckbox
					? PerPrintLimit
					: parentPerPrintLimit || MAXIMUM_LIMIT_AMOUNT,
			},

			/**
			 * Dev note:
			 * When user assigns the location a region, it means associating the location and the region. Hence,
			 * the location's id (refer to MeterAccount.CorpId) will be the same as region's id, and its
			 * ParentCorpId will be the corporation id.
			 *
			 * When user sets 'None' to the location, it means associating the location and the corporation.
			 * Hence, location's id (refer to MeterAccount.CorpId) will be the same as the corporation's id, and
			 * its ParentCorpId is `null`.
			 */
			ParentCorpId: CorpId === rootCorporationId ? null : ParentCorpId,
		};

		if (Enabled) {
			this.setState({
				isLoading: true,
			});

			const response = await AccountCommunication.getMaxPostageBalance(CustId);
			const { data = {} } = response;
			const maxPostageBalance = data.ResellerBalanceLimit
				? data.ResellerBalanceLimit
				: data.MeterBalanceLimit;

			this.setState({
				isLoading: false,
			});

			if (!AccountCommunication.isSuccessful(response)) {
				return showError({
					response,
				});
			}

			if (
				parseFloat(PurchaseAmount) + parseFloat(ThresholdAmount) >
				parseFloat(maxPostageBalance)
			) {
				return showError({
					data: {
						title: 'Max Postage Error',
						description: `This meter’s maximum postage balance is ${maxPostageBalance}. Postage purchases cannot exceed this amount.`,
						handleType: 'custom',
					},
					closeCallback: this.resetAutoPurchaseAndMeterAccountProperties,
				});
			}
		}

		this.updateLocations(updatedEnterpriseLocation);
	};

	handlePrintLimit = e => {
		const { value } = e.target;

		this.setState({
			PerPrintLimit: value,
		});
	};

	render() {
		const { enterpriseLocation, MAXIMUM_LIMIT_AMOUNT } = this.props;
		const {
			// AutoPurchase properties
			Enabled,
			PurchaseAmount,
			ThresholdAmount,
			// MeterAccount properties
			CorpId,
			Name,
			// for UserCaps
			ucPurchasePostage,
			ucPurchaseFromStore,
			referenceCodeRequired,
			restrictToCheckbox,
			ucWebBasedReports,
			PerPrintLimit,

			// Region dropdown
			regions,

			isLoading,
		} = this.state;
		const { MeterAccount } = enterpriseLocation;
		const { MeterNo } = MeterAccount;
		const userRole = Session.getUserRole();

		// only render Regions dropdown for Corp Admin users
		const isCorpAdmin = userRole === UserRole.CorpAdmin;

		return (
			<div className="account-locations location-general-settings">
				<div className="header">
					<h1>Location Settings</h1>
				</div>
				<div className="p-grid">
					<div className="p-col-6">
						<div className="common-ui-element-wrapper">
							<div className="label-container">ID:</div>
							<div className="input-container">{MeterNo}</div>
						</div>
						<WrappedInputTextWithLabel
							className="inputtext"
							label="Name:"
							uiElementConfig={{
								type: 'text',
								name: 'Name',
								value: Name,
								maxLength: 50,
								required: true,
								dataqa: 'locationname',
								onChange: this.handleInputChange,
							}}
						/>
						{isCorpAdmin && (
							<WrappedDropdownWithLabel
								className="dropdown"
								label="Region:"
								uiElementConfig={{
									name: 'CorpId',
									value: CorpId,
									options: regions,
									optionLabel: 'Name',
									optionValue: 'Id',
									dataqa: 'locationregion',
									onChange: this.handleInputChange,
								}}
							/>
						)}
					</div>
				</div>
				<div className="header">
					<h1>Purchase Settings</h1>
				</div>
				<div className="p-grid">
					<div className="p-col-12">
						<WrappedCheckbox
							name="ucPurchasePostage"
							checked={ucPurchasePostage}
							onChange={this.onClickCheckbox}
							dataqa="allowaddfundscheckbox"
						>
							Allow location to add funds
						</WrappedCheckbox>
					</div>
					<div className="p-col-fixed">
						<WrappedCheckbox
							name="Enabled"
							checked={Enabled}
							onChange={this.onClickCheckbox}
							dataqa="addfundscheckbox"
						>
							Add $
						</WrappedCheckbox>
					</div>
					<div className="p-col-fixed">
						<WrappedInputText
							className="inputtext dollar-amount-field"
							name="PurchaseAmount"
							value={PurchaseAmount}
							maxLength="5"
							dataqa="purchaseamount"
							onChange={this.handleInputChange}
						/>
					</div>
					<div className="p-col-fixed verticalTextAlign">
						when balance falls below $
					</div>
					<div className="p-col">
						<WrappedInputText
							className="inputtext dollar-amount-field"
							name="ThresholdAmount"
							value={ThresholdAmount}
							maxLength="5"
							dataqa="thresholdamount"
							onChange={this.handleInputChange}
						/>
					</div>
					<div className="p-col-12">
						<WrappedCheckbox
							name="ucPurchaseFromStore"
							checked={ucPurchaseFromStore}
							onChange={this.onClickCheckbox}
							dataqa="allowbuysuppliescheckbox"
						>
							Allow location to buy supplies
						</WrappedCheckbox>
					</div>
				</div>
				<div className="header">
					<h1>Print Settings</h1>
				</div>
				<div className="p-grid">
					<div className="p-col-12">
						<WrappedCheckbox
							name="referenceCodeRequired"
							checked={referenceCodeRequired}
							onChange={this.onClickCheckbox}
							dataqa="requirecostcodescheckbox"
						>
							Require location to use cost codes
						</WrappedCheckbox>
					</div>
					<div className="p-col-fixed">
						<WrappedCheckbox
							name="restrictToCheckbox"
							checked={restrictToCheckbox}
							onChange={this.onClickCheckbox}
							dataqa="restricttocheckbox"
						>
							Restrict to
						</WrappedCheckbox>
					</div>
					<div className="p-col">
						<WrappedInputNumberWithLabel
							className="inputtext dollar-amount-field"
							label=""
							uiElementConfig={{
								name: 'PerPrintLimit',
								value: !restrictToCheckbox ? null : PerPrintLimit,
								step: 1,
								min: 0,
								max: MAXIMUM_LIMIT_AMOUNT,
								minFractionDigits: 2,
								maxFracionDigits: 2,
								mode: 'currency',
								currency: 'USD',
								locale: 'en-US',
								dataqa: 'restricttoamount',
								disabled: !restrictToCheckbox,
								showButtons: true,
								onChange: this.handlePrintLimit,
							}}
						/>
					</div>
				</div>
				<div className="header">
					<h1>Report Settings</h1>
				</div>
				<div className="p-grid">
					<div className="p-col-6">
						<WrappedCheckbox
							name="ucWebBasedReports"
							checked={ucWebBasedReports}
							onChange={this.onClickCheckbox}
							dataqa="allowviewreportscheckbox"
						>
							Allow location to view reports
						</WrappedCheckbox>
					</div>
				</div>
				<div className="p-grid">
					<div className="p-col-6">
						<WrappedButton
							className="save-button"
							label="Save"
							disabled={isLoading}
							onClick={this.onSaveButtonClick}
							dataqa="location-settings-save"
						/>
					</div>
				</div>
			</div>
		);
	}
}

export default withLayoutAndModalContexts(LocationGeneralSettings);
